import styled, { keyframes, css } from "styled-components";
import { colors2 } from "oolib";

const shimmerSweep = keyframes`
  from { left: -100%; }
  to { left: 100%; }
`;

const caretBlink = keyframes`
  0%, 55% { opacity: 1; }
  56%, 100% { opacity: 0; }
`;

/**
 * One-line live status. The text replaces itself in place; the same shimmer
 * sweep the chat's loader already uses carries "working" without a spinner
 * farm. Motion is removed entirely under prefers-reduced-motion — the text
 * itself is the status.
 */
export const StatusLineStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  min-height: 2.4rem;

  /* LoaderCircleV2's isBlock renders full-width; unpinned it swallows the
     row's flexible space and squeezes the status text to zero width (the
     old loader pinned it inside a 1.5rem grid column for the same reason). */
  .status-spinner {
    flex: none;
    width: 1.6rem;
    display: inline-flex;
    align-items: center;
  }

  /* The status is always ONE line: it replaces itself, never wraps.
     min-width: 0 lets it ellipsize inside the flex row instead of
     squeezing the spinner and Stop button. */
  .status-text {
    position: relative;
    overflow: hidden;
    flex: 1;
    min-width: 0;
    color: ${colors2.grey80};

    p,
    span,
    div {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin: 0;
    }
  }

  /* Same sweep geometry as the chat's existing loader: a text-width band
     travelling from -100% to 100% — never a resident overlay. */
  .status-text::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      to right,
      transparent 0%,
      ${colors2.white} 30%,
      transparent 100%
    );
    animation: ${shimmerSweep} 2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
    pointer-events: none;
  }

  @media (prefers-reduced-motion: reduce) {
    .status-text::after {
      animation: none;
      content: none;
    }
  }
`;

/**
 * The investigation trail. Collapsed: a single quiet caption row.
 * Expanded: the steps, hung off a 1px hairline so the trail reads as
 * an aside to the answer, never a sibling of it.
 */
export const TrailStyled = styled.div`
  .trail-toggle {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    border: none;
    background: transparent;
    padding: 0.2rem 0;
    cursor: pointer;
    color: ${colors2.grey60};
    border-radius: 4px;

    &:hover {
      color: ${colors2.grey80};
    }

    &:focus-visible {
      outline: 2px solid ${colors2.grey60};
      outline-offset: 2px;
    }

    .trail-caret {
      display: inline-flex;
      transition: transform 0.18s ease-out;
      transform: rotate(${({ expanded }) => (expanded ? "90deg" : "0deg")});
    }

    @media (prefers-reduced-motion: reduce) {
      .trail-caret {
        transition: none;
      }
    }
  }

  .trail-steps {
    margin-top: 0.6rem;
    margin-left: 0.5rem;
    padding-left: 1.2rem;
    border-left: 1px solid ${colors2.grey10};
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .trail-step-tools {
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    margin-top: 0.3rem;
  }

  .trail-tool-line {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    color: ${colors2.grey60};
  }

  .trail-tool-error {
    color: ${colors2.red};
  }
`;

/**
 * Streamed markdown area. The caret rides the last rendered block via
 * ::after so it survives markdown re-renders; it stops blinking (and is
 * removed once done) rather than animating forever.
 */
export const StreamedAnswerStyled = styled.div`
  ${({ streaming }) =>
    streaming
      ? css`
          .md-stream > div > *:last-child::after {
            content: "▍";
            margin-left: 0.1rem;
            color: ${colors2.grey40};
            animation: ${caretBlink} 1.1s steps(1) infinite;
          }

          @media (prefers-reduced-motion: reduce) {
            .md-stream > div > *:last-child::after {
              animation: none;
            }
          }
        `
      : ""}
`;
